import type { RouteLocationNormalized } from 'vue-router';
import { ResourceType, type AuthorizationConfig, type RouteAuthConfig } from '../types/common';
import { Action } from '../types/roles';
import { NEW_ITEM_ID, PATH_VIEW, PATH_EDIT, PATH_NEW } from '../constants/common';
import { VinceApp } from '../composables/usePermission';


const isWriteAction = (route: RouteLocationNormalized, param?: string): boolean => {
    if (route.path.includes(PATH_VIEW)) { 
        return false; 
    }
    if (route.path.includes(PATH_EDIT) || route.path.includes(PATH_NEW)) { 
        return true; 
    }
    if (param) {
        const value = route.params[param];
        const paramValue = Array.isArray(value) ? value[0] : value;
        return paramValue === NEW_ITEM_ID;
    }
    return false;
};
interface ConfigOptions {
    resourceType: ResourceType;
    getAction: (r: RouteLocationNormalized) => Action;
    vinceApp?: VinceApp;
    getResourceName?: (r: RouteLocationNormalized) => string;
}

const config = (options: ConfigOptions): RouteAuthConfig => ({
    resourceType: options.resourceType,
    getAction: options.getAction,
    getResourceName: options.getResourceName ?? (() => '*'),
    vinceApp: options.vinceApp
});
const readWrite = (r: RouteLocationNormalized, param?: string): Action => 
    isWriteAction(r, param) ? Action.WRITE : Action.READ;
const routeMap: Record<string, RouteAuthConfig> = {
    '/ai-apps': config({
        resourceType: ResourceType.APP,
        getAction: () => Action.READ,
        vinceApp: VinceApp.AI_APPS
    }),
    '/tenant-prompt': config({
        resourceType: ResourceType.TENANT_PROMPT,
        getAction: () => Action.READ,
        vinceApp: VinceApp.AI_APPS
    }),
    '/mcp-servers': config({
        resourceType: ResourceType.MCP_SERVER,
        getAction: () => Action.READ,
        vinceApp: VinceApp.AI_APPS
    }),
    '/mcp-permissions': config({
        resourceType: ResourceType.MCP_TOOL_PERMISSION,
        getAction: () => Action.READ,
        vinceApp: VinceApp.AI_APPS
    }),
    '/access-management/users': config({
        resourceType: ResourceType.USER,
        getAction: r => readWrite(r, 'userId')
    }),
    '/access-management/roles': config({
        resourceType: ResourceType.ROLE,
        getAction: r => readWrite(r, 'roleId')
    }),
    '/access-management/api-clients': config({
        resourceType: ResourceType.APICLIENT,
        getAction: r => readWrite(r, 'apiClientId')
    }),
    '/environments': config({
        resourceType: ResourceType.ENVIRONMENT,
        getAction: r => readWrite(r, 'environmentId')
    }),
    '/connections': config({
        resourceType: ResourceType.CONNECTION,
        getAction: r => readWrite(r, 'connectionId')
    }),
    '/import-api': config({
        resourceType: ResourceType.CONNECTION,
        getAction: () => Action.WRITE
    }),
    '/variables': config({
        resourceType: ResourceType.VARIABLE,
        getAction: r => readWrite(r, 'variablesId')
    }),
    '/workflows': config({
        resourceType: ResourceType.WORKFLOW,
        getAction: r => readWrite(r, 'workflowId')
    }),
    '/workflow-statistics': config({
        resourceType: ResourceType.WORKFLOW,
        getAction: () => Action.READ
    }),
    '/workflow-monitoring': config({
        resourceType: ResourceType.WORKFLOW,
        getAction: () => Action.READ
    }),
    '/schedules': config({
        resourceType: ResourceType.WORKFLOW,
        getAction: () => Action.READ
    }),
    '/groups': config({
        resourceType: ResourceType.GROUP,
        getAction: r => readWrite(r, 'groupId')
    }),
    '/webhooks': config({
        resourceType: ResourceType.WEBHOOK,
        getAction: r => readWrite(r, 'webhookId')
    }),
    '/dashboards': config({
        resourceType: ResourceType.DASHBOARD,
        getAction: r => readWrite(r, 'dashboardId')
    }),
    '/company': config({
        resourceType: ResourceType.TENANT,
        getAction: () => Action.READ
    }),
    '/audit-logs': config({
        resourceType: ResourceType.TENANT,
        getAction: () => Action.READ
    }),
    '/tables': config({
        resourceType: ResourceType.META,
        getAction: () => Action.READ,
        vinceApp: VinceApp.CUSTOM_TABLES
    }),
    '/tools/m3-metadata': config({
        resourceType: ResourceType.WORKFLOW,
        getAction: () => Action.WRITE
    }),
    '/access-management/tenants': config({
        resourceType: ResourceType.TENANT,
        getAction: r => readWrite(r, 'tenantId'),
        vinceApp: VinceApp.SYS_ADMIN
    })
};
export function getAuthorizationConfig (route: RouteLocationNormalized): AuthorizationConfig | null {
    if (route.meta.authorize) { 
        return route.meta.authorize as AuthorizationConfig; 
    }
    const entry = Object.entries(routeMap)
        .filter(([path]) => route.path.startsWith(path))
        .sort((a, b) => b[0].length - a[0].length)[0];
    if (!entry) { 
        return null; 
    }
    const [, matched] = entry;
    return {
        resourceType: matched.resourceType,
        action: matched.getAction(route),
        resourceName: matched.getResourceName ? matched.getResourceName(route) : '*',
        vinceApp: matched.vinceApp
    };
}
